import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Comment, User } from '../types';
import type { CommentWithAuthor } from './postComments';

export { MAX_COMMENT_DEPTH, buildCommentTree, canReplyToComment } from './postComments';
export type { CommentWithAuthor, CommentNode } from './postComments';

export async function loadPaperComments(paperId: string): Promise<CommentWithAuthor[]> {
  const firestore = db;
  if (!firestore) return [];
  const qy = query(
    collection(firestore, 'newsPapers', paperId, 'comments'),
    orderBy('createdAt', 'asc'),
    limit(120)
  );
  let snap;
  try {
    snap = await getDocs(qy);
  } catch {
    return [];
  }
  const list: Comment[] = snap.docs.map((d) => ({
    id: d.id,
    // Set postId to paperId for TypeScript compatibility with Comment type
    postId: paperId,
    ...(d.data() as Omit<Comment, 'id' | 'postId'>),
  }));

  const authorIds = Array.from(new Set(list.map((c) => c.authorId)));
  const names = new Map<string, string>();
  await Promise.all(
    authorIds.map(async (uid) => {
      try {
        const uSnap = await getDoc(doc(firestore, 'users', uid));
        if (uSnap.exists()) {
          const data = uSnap.data() as Omit<User, 'uid'>;
          names.set(uid, data.name?.trim() || 'Member');
        } else {
          names.set(uid, 'Member');
        }
      } catch {
        names.set(uid, 'Member');
      }
    })
  );

  return list.map((c) => ({
    ...c,
    authorName: names.get(c.authorId) ?? 'Member',
  }));
}
